import axios, { CancelTokenSource } from "axios";
import { paths } from "config/paths";
import { useFormik } from "formik";
import { useWebRouter } from "~utils/next-router/use-web-router";
import { sendSignUpEvent } from "tag-manager";
import {
	SignUpFormValues,
	SignUpRemoteFormValues,
	SignUpRemoteSchema,
	SignUpSchema,
	toast,
	SignUp as View,
} from "ui-components";
import { useBoolean } from "usehooks-ts";
import { apiInstance, tryCatch } from "utils";

import { usePageLoaded } from "~auth/hooks/usePageLoaded";
import { useSignUp } from "~auth/queries/useSignUp";

let emailValidationSource: CancelTokenSource | null = null;

export const SignUp = () => {
	const isPageLoaded = usePageLoaded();
	const router = useWebRouter();
	const signUp = useSignUp();
	const form = useFormik<SignUpFormValues>({
		validateOnMount: true,
		initialValues: {
			name: "",
			password: "",
			acceptedToTermsAndPolicy: false,
		},
		validationSchema: SignUpSchema,
		validate: (values) => {
			if (values.name) {
				const error = validateNameForMaliciousContent(values.name);
				if (error) {
					return {
						name: error,
					};
				}
			}
			return {};
		},
		onSubmit: (values, helpers) => {
			const payload = {
				...values,
				...remoteForm.values,
				email: remoteForm.values.email.toLowerCase(),
			};

			toast.promise(
				async () => {
					const [error] = await tryCatch(signUp.mutateAsync(payload));
					if (error) {
						helpers.setSubmitting(false);
						throw error;
					}

					sendSignUpEvent();

					void router.replace(
						paths.signUpVerification + "?email=" + payload.email,
					);
				},
				{
					loading: "Signing you up...",
					success: "Success!",
					error: (e) =>
						axios.isAxiosError(e) ? "Something went wrong!" : e.message,
				},
			);
		},
	});
	const remoteForm = useFormik<SignUpRemoteFormValues>({
		validateOnMount: true,
		validateOnBlur: false,
		validateOnChange: false,
		initialValues: {
			email: "",
		},
		validationSchema: SignUpRemoteSchema,
		// eslint-disable-next-line @typescript-eslint/no-empty-function -- only use this form for validation
		onSubmit: () => {},
	});
	const { value: isRemotelyValidatingEmail, setValue: toggleValidatingEmail } =
		useBoolean(false);

	const handleEmailValueChange = async (newValue: string) => {
		const [schemaErrorMessage] = await Promise.all([
			SignUpRemoteSchema.fields.email
				.validate(newValue)
				.then(() => null)
				.catch((e) => e.message as string),
			remoteForm.setFieldValue("email", newValue),
			remoteForm.setFieldTouched("email", true),
		]);
		if (schemaErrorMessage) {
			remoteForm.setFieldError("email", schemaErrorMessage);
			return;
		}

		toggleValidatingEmail(true);
		const [remoteError] = await tryCatch(remotelyValidateEmail(newValue));
		if (remoteError) {
			if (axios.isCancel(remoteError)) return;

			toggleValidatingEmail(false);
			remoteForm.setFieldError("email", remoteError.message);
			return;
		}
		toggleValidatingEmail(false);
		remoteForm.setFieldError("email", undefined);
	};

	if (!isPageLoaded) return null;

	return (
		<View
			form={form}
			remoteForm={remoteForm}
			onEmailChange={(e) => handleEmailValueChange(e.target.value)}
			onEmailBlur={(e) => handleEmailValueChange(e.target.value)}
			// onNameBlur={(e) => handleNameBlur(e.target.value)}
			isRemotelyValidatingEmail={isRemotelyValidatingEmail}
		/>
	);
};

const remotelyValidateEmail = async (value: string) => {
	if (emailValidationSource) emailValidationSource.cancel();
	emailValidationSource = axios.CancelToken.source();

	const [error, data] = await tryCatch(
		apiInstance.get<{
			portal_url: string | null;
			status: string;
			users: Record<string, any>[];
		}>(`/account/search?term=${encodeURIComponent(value)}&qc=email`, {
			cancelToken: emailValidationSource.token,
		}),
	);

	if (error) {
		if (axios.isCancel(error)) throw error;
		if (axios.isAxiosError(error)) throw new Error("Something went wrong!");
		throw new Error(error.message);
	}
	if (data.users.length > 0)
		throw new Error(
			"This email is already in use. Please use another email address.",
		);
};

const validateNameForMaliciousContent = (name: string): string | null => {
	// Prevent HTML tags, script injection, and other malicious patterns
	const maliciousPatterns = [
		/<[^>]*>/g, // HTML tags
		/javascript:/gi, // JavaScript protocol
		/on\w+\s*=/gi, // Event handlers (onclick, onerror, etc.)
		/\\x[0-9a-f]{2}/gi, // Hex encoding
		/&#/g, // HTML entities
	];

	// Check for malicious patterns
	for (const pattern of maliciousPatterns) {
		if (pattern.test(name)) {
			return "Name can only contain letters, numbers, spaces, and apostrophes";
		}
	}

	// Allow letters, numbers, spaces, apostrophes, hyphens, and common international characters
	// This supports names like "Mary-Jane", "O'Brien", "José", "François", etc.
	const validPattern = /^[\p{L}\p{N}\s'\-]+$/u;

	if (!validPattern.test(name)) {
		return "Name can only contain letters, numbers, spaces, and apostrophes";
	}

	return null;
};
